import type {
  AuthoredTileMapData,
  MapDefinition,
  MapObjectPlacement,
  PathLayer,
  TileData,
} from '../../model/gamestate/GameState';

export interface StaticTerrainRenderData {
  readonly sourceMap: MapDefinition;
  readonly width: number;
  readonly height: number;
  readonly gridType: string;
  readonly frameIds: readonly (string | null)[];
  readonly pathLayer?: readonly number[];
  readonly pathLayers?: readonly PathLayer[];
  readonly authoredTileMap?: AuthoredTileMapData | null;
}

export interface StaticObjectRenderItem {
  readonly id: string;
  readonly assetId: string;
  readonly mapX: number;
  readonly mapY: number;
  readonly offsetX: number;
  readonly offsetY: number;
  readonly sizeMultiplier: number;
  readonly flipHorizontal: boolean;
  readonly flipVertical: boolean;
  readonly rotationSteps: number;
  readonly layer: string | null;
}

export interface StaticObjectRenderData {
  readonly items: readonly StaticObjectRenderItem[];
}

export interface DynamicActorRenderItem {
  readonly id: string;
  readonly playerId?: string;
  readonly kind: 'self' | 'remotePlayer' | 'mob';
  readonly entityType?: string;
  readonly displayName?: string;
  readonly guildName?: string;
  readonly accessoriesJson?: string;
  readonly x: number;
  readonly y: number;
  readonly worldCenterX?: number;
  readonly worldCenterY?: number;
  readonly rotation: number;
  readonly color: string;
  readonly alpha?: number;
  readonly isMoving?: boolean;
  readonly hp?: number;
  readonly maxHp?: number;
  readonly stamina?: number;
  readonly maxStamina?: number;
  readonly mobInventoryValue?: number | null;
  readonly mobInventoryItemsJson?: string | null;
}

export interface DynamicMarkerRenderItem {
  readonly id: string;
  readonly kind: 'npc' | 'chest';
  readonly x: number;
  readonly y: number;
  readonly label?: string;
}

export interface DynamicSceneRenderData {
  readonly actors: readonly DynamicActorRenderItem[];
  readonly markers: readonly DynamicMarkerRenderItem[];
}

export function buildStaticTerrainRenderData(map: MapDefinition): StaticTerrainRenderData {
  const frameIds: (string | null)[] = new Array(map.width * map.height).fill(null);
  for (let y = 0; y < map.height; y += 1) {
    for (let x = 0; x < map.width; x += 1) {
      frameIds[(y * map.width) + x] = resolveTerrainAssetId(map, map.tiles[y]?.[x] ?? null);
    }
  }
  return {
    sourceMap: map,
    width: map.width,
    height: map.height,
    gridType: map.gridType,
    frameIds,
    pathLayer: map.pathLayer,
    pathLayers: map.pathLayers,
    authoredTileMap: map.authoredTileMap,
  };
}

export function buildStaticObjectRenderData(map: MapDefinition): StaticObjectRenderData {
  return {
    items: map.objects.map(normalizeObjectPlacement),
  };
}

function resolveTerrainAssetId(map: MapDefinition, tile: TileData | null): string | null {
  if (!tile) {
    return null;
  }
  const biome = tile.biome ?? map.defaultBiome ?? 'grass';
  return biome === 'default' ? 'grass' : biome;
}

function normalizeObjectPlacement(placement: MapObjectPlacement): StaticObjectRenderItem {
  return {
    id: placement.id,
    assetId: placement.asset ?? '',
    mapX: placement.x,
    mapY: placement.y,
    offsetX: placement.offsetX ?? 0,
    offsetY: placement.offsetY ?? 0,
    sizeMultiplier: placement.sizeMultiplier ?? 1,
    flipHorizontal: placement.flipHorizontal === true,
    flipVertical: placement.flipVertical === true,
    rotationSteps: placement.rotationSteps ?? 0,
    layer: placement.layer ?? null,
  };
}
